import React, { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * ReportExportBar — Generic export + email bar for all reports.
 *
 * Props:
 *   onExportPdf    – async fn() → triggers PDF download (only rendered if provided)
 *   onExportCsv    – async fn() → triggers CSV download (only rendered if provided)
 *   onSendEmail    – async fn(email: string) → sends report via email (only rendered if provided)
 *   disabled       – boolean: disables all buttons (e.g. while report not yet generated)
 *   emailLabel     – optional label for the email input (default from i18n)
 */
export default function ReportExportBar({
    onExportPdf,
    onExportCsv,
    onSendEmail,
    disabled = false,
    emailLabel,
}) {
    const { t } = useTranslation();
    const resolvedEmailLabel = emailLabel ?? t("reports.exportBar.sendReportViaEmail");

    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [email, setEmail] = useState("");
    const [emailSuccess, setEmailSuccess] = useState(false);
    const [emailError, setEmailError] = useState("");

    const handleExportPdf = async () => {
        if (!onExportPdf || disabled || exportingPdf) return;
        setExportingPdf(true);
        try {
            await onExportPdf();
        } catch (e) {
            console.error("PDF export failed:", e);
            alert(t("reports.exportBar.exportPdfFailed"));
        } finally {
            setExportingPdf(false);
        }
    };

    const handleExportCsv = async () => {
        if (!onExportCsv || disabled || exportingCsv) return;
        setExportingCsv(true);
        try {
            await onExportCsv();
        } catch (e) {
            console.error("CSV export failed:", e);
            alert(t("reports.exportBar.exportCsvFailed"));
        } finally {
            setExportingCsv(false);
        }
    };

    const handleSendEmail = async () => {
        if (!onSendEmail || disabled || sendingEmail) return;
        if (!email || !email.trim()) {
            setEmailError(t("reports.exportBar.enterEmail"));
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setEmailError(t("reports.exportBar.enterValidEmail"));
            return;
        }
        setEmailError("");
        setSendingEmail(true);
        try {
            await onSendEmail(email.trim());
            setEmailSuccess(true);
            setEmail("");
            setTimeout(() => setEmailSuccess(false), 3000);
        } catch (e) {
            console.error("Email send failed:", e);
            setEmailError(t("reports.exportBar.sendEmailFailed"));
        } finally {
            setSendingEmail(false);
        }
    };

    const SpinnerIcon = () => (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const PdfIcon = () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
    );

    const CsvIcon = () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    );

    const EmailIcon = () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    );

    const CheckIcon = () => (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
    );

    const hasExportOptions = onExportPdf || onExportCsv;
    const hasEmailOption = !!onSendEmail;

    if (!hasExportOptions && !hasEmailOption) return null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap gap-3 items-center">
                {onExportPdf && (
                    <button
                        onClick={handleExportPdf}
                        disabled={disabled || exportingPdf}
                        className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    >
                        {exportingPdf ? <SpinnerIcon /> : <PdfIcon />}
                        {exportingPdf ? t("reports.exportBar.generatingPdf") : t("reports.exportBar.exportPdf")}
                    </button>
                )}

                {onExportCsv && (
                    <button
                        onClick={handleExportCsv}
                        disabled={disabled || exportingCsv}
                        className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    >
                        {exportingCsv ? <SpinnerIcon /> : <CsvIcon />}
                        {exportingCsv ? t("reports.exportBar.generatingCsv") : t("reports.exportBar.exportCsv")}
                    </button>
                )}

                {hasEmailOption && (
                    <div className="flex gap-2 items-center flex-1 min-w-[280px]">
                        <div className="flex-1">
                            <input
                                type="email"
                                placeholder={`✉️ ${resolvedEmailLabel}`}
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError("");
                                    setEmailSuccess(false);
                                }}
                                onKeyDown={(e) => e.key === "Enter" && handleSendEmail()}
                                className={`border p-2.5 rounded-lg w-full focus:ring-2 focus:ring-blue-500 text-sm ${emailError
                                        ? "border-red-400 focus:border-red-400 focus:ring-red-300"
                                        : "border-gray-300 focus:border-blue-500"
                                    }`}
                            />
                            {emailError && (
                                <p className="text-xs text-red-600 mt-1" role="status">{emailError}</p>
                            )}
                        </div>
                        <button
                            onClick={handleSendEmail}
                            disabled={disabled || sendingEmail}
                            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm whitespace-nowrap"
                        >
                            {sendingEmail ? (
                                <>
                                    <SpinnerIcon />
                                    {t("reports.exportBar.sending")}
                                </>
                            ) : emailSuccess ? (
                                <>
                                    <CheckIcon />
                                    {t("reports.exportBar.sent")}
                                </>
                            ) : (
                                <>
                                    <EmailIcon />
                                    {t("reports.exportBar.sendEmail")}
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {disabled && (
                <p className="text-xs text-gray-400 mt-2">
                    {t("reports.exportBar.generateFirstHint")}
                </p>
            )}
        </div>
    );
}
